/**
 * Phone Demo Stats
 *
 * This module computes statistics from mermaid diagrams for the phone demo display.
 * Extracted for testability and reuse.
 */

export interface DemoStat {
  label: string;
  text: string;
}

export interface MermaidParseResult {
  nodeCount: number;
  decisionPoints: number;
  maxDay: number;
}

export interface DemoMessage {
  label: string;
  source: 'brand' | 'customer';
  text: string;
}

/**
 * Parse mermaid diagram to extract node count and decision points
 */
export function parseMermaidDiagram(mermaidDiagram: string | null): MermaidParseResult {
  if (!mermaidDiagram) {
    return { nodeCount: 0, decisionPoints: 0, maxDay: 0 };
  }

  // Count nodes by finding node definitions (id["text"] or id("text") patterns)
  const nodePattern = /^\s*(\w+)[\["(]/gm;
  const nodes = new Set<string>();
  let match;
  while ((match = nodePattern.exec(mermaidDiagram)) !== null) {
    nodes.add(match[1]);
  }

  // Count decision points (nodes with multiple outgoing connections)
  // Find all connections: nodeA --> nodeB or nodeA -->|label| nodeB
  const connectionPattern = /^\s*(\w+)\s*-->/gm;
  const outgoingCounts: Record<string, number> = {};
  while ((match = connectionPattern.exec(mermaidDiagram)) !== null) {
    const sourceNode = match[1];
    outgoingCounts[sourceNode] = (outgoingCounts[sourceNode] || 0) + 1;
  }

  // Decision points are nodes with more than one outgoing connection
  const decisionPoints = Object.values(outgoingCounts).filter(count => count > 1).length;

  // Find max day from node labels like "Day 1" or "Day 30"
  const dayPattern = /Day\s*(\d+)/gi;
  let maxDay = 0;
  while ((match = dayPattern.exec(mermaidDiagram)) !== null) {
    const day = parseInt(match[1], 10);
    if (day > maxDay) maxDay = day;
  }

  return {
    nodeCount: nodes.size,
    decisionPoints,
    maxDay
  };
}

/**
 * Compute display stats from mermaid diagram and conversation data
 */
export function computeStats(mermaidDiagram: string | null, conversation: DemoMessage[] | null): DemoStat[] {
  const { nodeCount, decisionPoints, maxDay } = parseMermaidDiagram(mermaidDiagram);

  // Count brand messages in conversation (happy path)
  const happyPathMessageCount = conversation
    ? conversation.filter(msg => msg.source === 'brand').length
    : 0;

  const stats: DemoStat[] = [];

  // Touchpoints (all possible messages in the journey)
  if (nodeCount > 0) {
    stats.push({
      label: `${nodeCount} Touchpoints`,
      text: 'Personalized to each customer'
    });
  }

  // Happy path messages (from conversation demo)
  if (happyPathMessageCount > 0 && maxDay > 0) {
    stats.push({
      label: `${happyPathMessageCount} Messages`,
      text: `Typically sent across ${maxDay} days`
    });
  } else if (happyPathMessageCount > 0) {
    stats.push({
      label: `${happyPathMessageCount} Messages`,
      text: 'Typically sent per customer'
    });
  }

  // Decision points
  if (decisionPoints > 0) {
    stats.push({
      label: `${decisionPoints} Decision Points`,
      text: 'Branching logic for personalization'
    });
  }

  return stats;
}
